import { createSlice, createAction, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { RootState } from 'app/state/store'
import { RequestStatus } from 'features/common'
import {
  ImportingJobPayload,
  JobBase,
  JobStatusType,
  LayerExportJob,
  ActivityDataExportJob,
  ProcessingJob,
  ProcessingJobPayload,
  ProcessingJobStatus
} from '../types'
import { isPendingOrRunning } from '../models/jobs'
export const name = 'exportingJobs'
export const CREATE_EXPORTING_JOB_REQUEST = `${name}/create_EXPORTING_job_request`
export const CANCEL_EXPORTING_JOB_REQUEST = `${name}/cancel_EXPORTING_job_request`
export const GET_EXPORTING_JOBS_REQUEST = `${name}/get_EXPORTING_jobs_request`
export const GET_USER_EXPORTING_JOBS_REQUEST = `${name}/get_user_EXPORTING_jobs_request`
export const CHECK_EXPORTING_JOB_REQUEST = `${name}/check_EXPORTING_job_request`
export const FILTER_EXPORTING_JOBS_REQUEST = `${name}/filter_EXPORTING_jobs_request`

export interface JobsState {
  exports: (LayerExportJob | ActivityDataExportJob)[]
  exportingJobs: Array<ProcessingJob>
  exportingJobsStatus: RequestStatus
  exportingJobsInProgress: string[]
  createExportingJobStatus: RequestStatus
  lastExportingJobDone?: ProcessingJob
  currentExportingJob?: ProcessingJob
}

const initialState: JobsState = {
  exports: [],
  exportingJobs: [],
  exportingJobsStatus: RequestStatus.Idle,
  exportingJobsInProgress: [],
  createExportingJobStatus: RequestStatus.Idle
}

const asyncActions = {
  createExportingJobRequest: createAction<{
    connection: string
    name?: string
    processingJobPayload: ProcessingJobPayload
  }>(CREATE_EXPORTING_JOB_REQUEST),
  deleteExportingJobRequest: createAction<{ job: ProcessingJob }>(CANCEL_EXPORTING_JOB_REQUEST),
  getExportingJobsRequest: createAction<ImportingJobPayload>(GET_EXPORTING_JOBS_REQUEST),
  checkExportingJobStatusRequest: createAction<ProcessingJob[]>(CHECK_EXPORTING_JOB_REQUEST),
  clearExportingJobsRequest: createAction<ProcessingJob[]>(FILTER_EXPORTING_JOBS_REQUEST)
}

type UpdateExportJobPayload = Pick<JobBase, 'id'> & Partial<LayerExportJob | ActivityDataExportJob>

const reducers = {
  setExports: (state: JobsState, action: PayloadAction<(LayerExportJob | ActivityDataExportJob)[]>) => {
    const payloadSorted = [...action.payload]
    payloadSorted.sort((a, b) => b.createdAt - a.createdAt)
    state.exports = payloadSorted
  },
  addExportJob: (state: JobsState, action: PayloadAction<LayerExportJob | ActivityDataExportJob>) => {
    const tmpJobs = [...state.exports]
    tmpJobs.unshift(action.payload)
    state.exports = tmpJobs
  },
  updateExportJob: (state: JobsState, action: PayloadAction<UpdateExportJobPayload>) => {
    const tmpJobs = [...state.exports]
    const index = tmpJobs.findIndex((job) => job.id === action.payload.id)
    if (index === -1) {
      tmpJobs.unshift(action.payload as LayerExportJob)
    } else {
      // don' override status if it's cancelled
      if (tmpJobs[index].status === JobStatusType.Cancelled) return

      tmpJobs[index] = { ...tmpJobs[index], ...action.payload } as LayerExportJob
    }
    state.exports = tmpJobs
  },
  removeExportJob: (state: JobsState, action: PayloadAction<string>) => {
    const removedTaskId = action.payload
    state.exports = state.exports.filter((task) => task.id !== removedTaskId)
  },
  addJob: (state: JobsState, action: PayloadAction<ProcessingJob>) => {
    state.exportingJobs = [action.payload, ...state.exportingJobs]
  },

  updateJob: (state: JobsState, action: PayloadAction<ProcessingJobStatus>) => {
    const tempExportingJobs = [...state.exportingJobs]
    tempExportingJobs.forEach((j) => {
      if (j.response.externalId === action.payload.jobId) j.status = action.payload
    })

    state.exportingJobs = tempExportingJobs
  },
  setExportingJobs: (state: JobsState, action: PayloadAction<Array<ProcessingJob>>) => {
    state.exportingJobs = action.payload
  },
  removeExportingJobs: (state: JobsState, action: PayloadAction<Array<ProcessingJob>>) => {
    const removeIds = action.payload.map((job: ProcessingJob) => job.jobId)
    state.exportingJobs = state.exportingJobs.filter((job: ProcessingJob) => !removeIds.includes(job.jobId))
  },
  setExportingJobsStatus: (state: JobsState, action: PayloadAction<RequestStatus>) => {
    state.exportingJobsStatus = action.payload
  },
  setCreateExportingJobStatus: (state: JobsState, action: PayloadAction<RequestStatus>) => {
    state.createExportingJobStatus = action.payload
  },
  setLastExportingJobDone: (state: JobsState, action: PayloadAction<ProcessingJob | undefined>) => {
    state.lastExportingJobDone = action.payload
  },
  removeFromExportingJobs: (state: JobsState, action: PayloadAction<string>) => {
    state.exportingJobs = [...state.exportingJobs.filter((job) => job.jobId !== action.payload)]
  },
  addExportingJobInProgress: (state: JobsState, action: PayloadAction<string>) => {
    state.exportingJobsInProgress.push(action.payload)
  },
  removeExportingJobInProgress: (state: JobsState, action: PayloadAction<string>) => {
    state.exportingJobsInProgress.filter((jobId) => jobId !== action.payload)
  },
  setCurrentExportingJob: (state: JobsState, action: PayloadAction<ProcessingJob | undefined>) => {
    state.currentExportingJob = action.payload
  }
}

export const MapsSlice = createSlice({
  name,
  initialState,
  reducers
})

export const exportingJobsInProgress = createSelector(
  (state: RootState) => state.jobsExporting.exportingJobs,
  (exportingJobs) => exportingJobs.filter(isPendingOrRunning)
)

export const areExportingJobsInProgress = createSelector(
  (state: RootState) => state.jobsExporting.exportingJobs,
  (exportingJobs) => exportingJobs.some(isPendingOrRunning)
)

export const {
  setExports,
  addExportJob,
  updateExportJob,
  removeExportJob,
  addJob,
  updateJob,
  setExportingJobs,
  removeExportingJobs,
  setExportingJobsStatus,
  setCreateExportingJobStatus,
  setLastExportingJobDone,
  addExportingJobInProgress,
  removeExportingJobInProgress,
  removeFromExportingJobs,
  setCurrentExportingJob
} = MapsSlice.actions

export const {
  createExportingJobRequest,
  getExportingJobsRequest,
  checkExportingJobStatusRequest,
  deleteExportingJobRequest,
  clearExportingJobsRequest
} = asyncActions

export default MapsSlice.reducer
